// src/components/administration/manifest/MonitoringDashboard.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
    Alert, Badge, Box, Button, Card, CardContent, Chip, CircularProgress,
    Divider, IconButton, InputAdornment, LinearProgress, Paper, Stack,
    TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PersonIcon from "@mui/icons-material/Person";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import SignalCellularOffIcon from "@mui/icons-material/SignalCellularOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PlaceIcon from "@mui/icons-material/Place";
import RouteIcon from "@mui/icons-material/Route";
import SpeedIcon from "@mui/icons-material/Speed";
import FlashOnIcon from "@mui/icons-material/FlashOn";

import {
    useActiveManifests,
    useMonitoringStream,
    useManifestPlannedRoute,
    useManifestLiveRoute,
    useManifestMatchedTrack,
    deriveManifestStatus,
    computeHealth,
    classifySpeed,
    isSpeeding,
} from "../../../hooks/gpsTracking/useGpsTracking";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const TZ = "America/Lima";
const DEFAULT_CENTER = [-77.0428, -12.0464];
const DEFAULT_ZOOM = 5;

/* ---------- CSS de markers (nombre + placa) ---------- */
const MAP_STYLES = `
  @keyframes gpsPulse {
    0%   { box-shadow: 0 0 0 0 rgba(16,185,129,.55); }
    70%  { box-shadow: 0 0 0 14px rgba(16,185,129,0); }
    100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
  }
  .gps-marker-wrap {
    position: relative;
    cursor: pointer;
    width: 16px;
    height: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .gps-marker-label {
    position: absolute;
    bottom: 22px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255,255,255,0.96);
    border-radius: 6px;
    padding: 3px 7px;
    box-shadow: 0 2px 6px rgba(0,0,0,.2);
    white-space: nowrap;
    font-size: 10px;
    line-height: 1.35;
    text-align: center;
    min-width: 85px;
    pointer-events: none;
  }
  .gps-marker-name {
    font-weight: 700; color: #0f172a;
    display: flex; align-items: center; justify-content: center; gap: 2px;
  }
  .gps-marker-name svg { width: 10px; height: 10px; color: #475569; }
  .gps-marker-plate {
    font-weight: 800; font-family: monospace; color: #64748b; margin-top: 1px;
    display: flex; align-items: center; justify-content: center; gap: 2px;
    font-size: 10px; letter-spacing: 0.3px;
  }
  .gps-marker-plate svg { width: 10px; height: 10px; color: #94a3b8; }
  .gps-marker-plate--speeding { color: #ef4444; }
  .gps-marker-plate--speeding svg { color: #ef4444; }
  .gps-marker-dot {
    width: 16px; height: 16px; border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,.35);
    transition: transform .15s ease, background .15s ease;
  }
  .gps-marker-dot--active   { background: #10b981; animation: gpsPulse 2s infinite; }
  .gps-marker-dot--stale    { background: #f59e0b; }
  .gps-marker-dot--noloc    { background: #9ca3af; }
  .gps-marker-dot--speeding { background: #ef4444; animation: gpsPulse 1s infinite; }
  .gps-marker-wrap--selected .gps-marker-dot { transform: scale(1.6); z-index: 10; }
`;

/* ---------- formatters ---------- */
const fmtRelative = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const diff = Date.now() - d.getTime();
    if (diff < 0) return "ahora";
    if (diff < 60_000) return "<1 min";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h`;
    return d.toLocaleString("es-PE", { timeZone: TZ });
};
const fmtDuration = (sec) => {
    if (!Number.isFinite(sec)) return "—";
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const whenStyleLoaded = (map, cb) => {
    if (!map) return () => { };
    if (map.isStyleLoaded()) { cb(); return () => { }; }
    let cancelled = false;
    const handler = () => {
        map.off("styledata", handler); map.off("load", handler);
        if (!cancelled) cb();
    };
    map.on("styledata", handler); map.on("load", handler);
    return () => {
        cancelled = true;
        try { map.off("styledata", handler); } catch { }
        try { map.off("load", handler); } catch { }
    };
};

const buildMatchedGeojson = (selMatched) => {
    if (!selMatched) return null;
    if (selMatched.format === "feature" && Array.isArray(selMatched.features) && selMatched.features.length > 0) {
        return { type: "FeatureCollection", features: selMatched.features };
    }
    if (Array.isArray(selMatched.coordinates) && selMatched.coordinates.length > 1) {
        const geom = selMatched.type === "MultiLineString"
            ? { type: "MultiLineString", coordinates: selMatched.coordinates }
            : { type: "LineString", coordinates: selMatched.coordinates };
        return { type: "Feature", properties: {}, geometry: geom };
    }
    return null;
};

/* ========================================================================== */
/* ManifestCard — UNA sola definición                                         */
/* ========================================================================== */
const ManifestCard = React.memo(function ManifestCard({ manifest, selected, onSelect, onOpen }) {
    const status = deriveManifestStatus(manifest);
    const health = computeHealth(manifest);
    const speed = manifest.lastLocation?.speedKmh;
    const speedClass = classifySpeed(speed);
    const speeding = isSpeeding(manifest);
    const accentColor = status.key === 'stale' ? 'warning.main'
        : speeding ? 'error.main'
            : status.key === 'active' ? 'success.main'
                : 'grey.500';

    const progressPct = manifest.stopsTotal > 0
        ? Math.round(((manifest.stopsTotal - manifest.stopsRemaining) / manifest.stopsTotal) * 100)
        : 0;

    // Conductor al mando: DRIVER activo > peer DRIVER > fallback
    const driverInfo = useMemo(() => {
        if (manifest.hasActiveDriver && manifest.rol === 'DRIVER') {
            return {
                nombre: manifest.empNombreCorto || manifest.empNombre,
                rol: 'DRIVER',
            };
        }
        const driverPeer = (manifest.peers || []).find((p) => p.rol === 'DRIVER');
        if (driverPeer) return { nombre: driverPeer.nombreCorto, rol: 'DRIVER' };
        return {
            nombre: manifest.empNombreCorto || manifest.empNombre || `Emp ${manifest.empId}`,
            rol: manifest.rol || '—',
        };
    }, [manifest]);

    const otherPeers = useMemo(() => {
        if (!Array.isArray(manifest.peers)) return [];
        return manifest.peers.filter((p) => p.rol !== 'DRIVER');
    }, [manifest]);

    return (
        <Paper
            elevation={0}
            onClick={() => onSelect(manifest.manId)}
            sx={{
                p: 1.75, borderRadius: 2, cursor: "pointer", position: "relative",
                border: "1px solid",
                borderColor: selected ? "primary.main" : speeding ? "error.light" : "divider",
                bgcolor: selected ? alpha('#1976d2', 0.04) : speeding ? alpha('#ef4444', 0.03) : "background.paper",
                transition: "all .15s ease",
                "&:hover": { borderColor: selected ? "primary.main" : "text.disabled", transform: "translateY(-1px)", boxShadow: 1 },
                "&::before": {
                    content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: 4,
                    bgcolor: accentColor, borderTopLeftRadius: 2, borderBottomLeftRadius: 2,
                },
            }}
        >
            {/* Conductor al mando */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                    <PersonIcon sx={{ fontSize: 18, color: driverInfo.rol === 'DRIVER' ? 'success.main' : 'warning.main' }} />
                    <Typography variant="body2" fontWeight={800} noWrap>
                        {driverInfo.nombre}
                    </Typography>
                    <Chip
                        size="small"
                        label={driverInfo.rol}
                        color={driverInfo.rol === 'DRIVER' ? 'success' : 'warning'}
                        variant="outlined"
                        sx={{ height: 16, fontSize: 9, fontWeight: 800 }}
                    />
                    {otherPeers.length > 0 && (
                        <Tooltip title={otherPeers.map((p) => `${p.nombreCorto} (${p.rol})`).join(', ')}>
                            <Chip size="small" label={`+${otherPeers.length}`} sx={{ height: 16, fontSize: 9 }} />
                        </Tooltip>
                    )}
                </Stack>
                <Chip size="small" label={status.label} color={status.color} sx={{ fontWeight: 800, fontSize: 10 }} />
            </Stack>

            {/* Vehículo + velocidad */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 0.75 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                    <LocalShippingIcon sx={{ fontSize: 18, color: accentColor }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 900, fontFamily: 'monospace', letterSpacing: 0.5, lineHeight: 1 }} noWrap>
                        {manifest.vehMatricula || `Veh ${manifest.vehId}`}
                    </Typography>
                    {(manifest.vehMarca || manifest.vehModelo) && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 140 }}>
                            {[manifest.vehMarca, manifest.vehModelo].filter(Boolean).join(' · ')}
                        </Typography>
                    )}
                </Stack>
                {speedClass && (
                    <Tooltip title={`${speedClass.label} · ${speed.toFixed(1)} km/h`}>
                        <Chip
                            size="small"
                            icon={speeding ? <FlashOnIcon sx={{ fontSize: 12 }} /> : <SpeedIcon sx={{ fontSize: 12 }} />}
                            label={`${Math.round(speed)} km/h`}
                            sx={{
                                bgcolor: speedClass.bg,
                                color: speedClass.color,
                                fontWeight: 800,
                                fontSize: 11,
                                border: speeding ? `1px solid ${speedClass.color}` : 'none',
                                animation: speeding ? 'gpsPulse 1s infinite' : 'none',
                            }}
                        />
                    </Tooltip>
                )}
            </Stack>

            {/* Ruta */}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1 }}>
                <RouteIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                    {manifest.manOri || '—'} → {manifest.manDes || '—'}
                </Typography>
            </Stack>

            {/* Progreso */}
            {manifest.stopsTotal > 0 && (
                <Box sx={{ mt: 0.75 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            {manifest.stopsTotal - manifest.stopsRemaining}/{manifest.stopsTotal} paradas
                        </Typography>
                        {manifest.nextStop?.nombre && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '60%', fontSize: 10 }}>
                                Próx: {manifest.nextStop.nombre}
                            </Typography>
                        )}
                    </Stack>
                    <LinearProgress
                        variant="determinate"
                        value={progressPct}
                        sx={{ height: 4, borderRadius: 1, bgcolor: 'grey.200' }}
                        color={progressPct === 100 ? 'success' : 'primary'}
                    />
                </Box>
            )}

            {/* Salud */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Tooltip title="Heartbeat del servidor">
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: health.hbFresh ? 'success.main' : 'warning.main' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                ❤ {fmtRelative(manifest.lastHeartbeatServer || manifest.lastHeartbeatClient)}
                            </Typography>
                        </Stack>
                    </Tooltip>
                    <Tooltip title="Último ping GPS">
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                            {health.gpsFresh ? (
                                <SignalCellularAltIcon sx={{ fontSize: 12, color: 'success.main' }} />
                            ) : (
                                <SignalCellularOffIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                {fmtRelative(manifest.lastLocation?.timestamp)}
                            </Typography>
                        </Stack>
                    </Tooltip>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <ScheduleIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                        {fmtDuration(manifest.sessionDurationSec)}
                    </Typography>
                    <Tooltip title="Abrir monitor detallado">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpen(manifest.manId); }}>
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {manifest.isStale && (
                <Alert severity="warning" sx={{ mt: 1, py: 0, px: 1, fontSize: 10, '& .MuiAlert-icon': { fontSize: 14 } }}>
                    Sin señal hace {fmtRelative(manifest.lastHeartbeatServer || manifest.lastHeartbeatClient)}
                </Alert>
            )}
            {speeding && (
                <Alert severity="error" sx={{ mt: 1, py: 0, px: 1, fontSize: 10, '& .MuiAlert-icon': { fontSize: 14 } }}>
                    <FlashOnIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                    Exceso: {Math.round(speed)} km/h
                </Alert>
            )}
            {manifest.handoverToEmpId && (
                <Alert severity="info" sx={{ mt: 1, py: 0, px: 1, fontSize: 10, '& .MuiAlert-icon': { fontSize: 14 } }}>
                    <SwapHorizIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                    Handover a Emp {manifest.handoverToEmpId}
                </Alert>
            )}
        </Paper>
    );
});

/* ========================================================================== */
/* Dashboard                                                                  */
/* ========================================================================== */
const MonitoringDashboard = ({ onOpenDetail }) => {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [filterMode, setFilterMode] = useState("all");
    const [selectedManId, setSelectedManId] = useState(null);

    const openDetail = useCallback((manId) => {
        if (typeof onOpenDetail === "function") return onOpenDetail(manId);
        navigate(`/administracion/manifiestos/${manId}/stream`);
    }, [navigate, onOpenDetail]);

    const { data: snapshot, isLoading, isFetching, error, refetch } =
        useActiveManifests({ enabled: true, refetchInterval: 30_000 });

    const { data: selPlanned } = useManifestPlannedRoute(selectedManId, !!selectedManId);
    const { data: selLive } = useManifestLiveRoute(selectedManId, { enabled: !!selectedManId });
    const { data: selMatched } = useManifestMatchedTrack(selectedManId, { enabled: !!selectedManId });
    const { byManId, status: streamStatus, error: streamError } = useMonitoringStream({ enabled: true });

    const manifests = useMemo(
        () => (Array.isArray(snapshot?.manifests) ? snapshot.manifests : []),
        [snapshot],
    );

    const locationsByManId = useMemo(() => {
        const map = {};
        for (const m of manifests) {
            if (m.lastLocation && typeof m.lastLocation.lat === "number") map[m.manId] = m.lastLocation;
        }
        for (const [manIdStr, loc] of Object.entries(byManId)) {
            const manId = Number(manIdStr);
            if (loc && typeof loc.lat === "number") map[manId] = loc;
        }
        return map;
    }, [manifests, byManId]);

    const stats = useMemo(() => {
        let active = 0, stale = 0, noloc = 0, speeding = 0, withHandover = 0, totalRemaining = 0;
        for (const m of manifests) {
            const s = deriveManifestStatus(m);
            if (s.key === 'active') active++;
            else if (s.key === 'stale') stale++;
            else if (s.key === 'noloc') noloc++;
            else if (s.key === 'speeding') { speeding++; active++; }
            if (m.handoverToEmpId) withHandover++;
            totalRemaining += m.stopsRemaining || 0;
        }
        return { total: manifests.length, active, stale, noloc, speeding, withHandover, totalRemaining };
    }, [manifests]);

    const visibleManifests = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = manifests.filter((m) => {
            const s = deriveManifestStatus(m);
            if (filterMode === "active" && s.key !== "active" && s.key !== "speeding") return false;
            if (filterMode === "stale" && s.key !== "stale") return false;
            if (filterMode === "noloc" && s.key !== "noloc") return false;
            if (filterMode === "speeding" && s.key !== "speeding") return false;
            if (filterMode === "handover" && !m.handoverToEmpId) return false;
            if (!q) return true;
            const haystack = [
                m.manId, m.vehMatricula, m.vehMarca, m.vehModelo,
                m.empNombre, m.empNombreCorto, m.empId, m.rol,
                m.manOri, m.manDes, m.nextStop?.nombre, m.proyecto,
            ].filter(Boolean).join(" ").toLowerCase();
            return haystack.includes(q);
        });
        return [...filtered].sort((a, b) => {
            const pa = deriveManifestStatus(a).priority;
            const pb = deriveManifestStatus(b).priority;
            if (pa !== pb) return pa - pb;
            const ta = a.lastHeartbeatServer ? new Date(a.lastHeartbeatServer).getTime() : 0;
            const tb = b.lastHeartbeatServer ? new Date(b.lastHeartbeatServer).getTime() : 0;
            return tb - ta;
        });
    }, [manifests, filterMode, query]);

    const mapContainer = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef(new Map());
    const hasAutoFittedRef = useRef(false);
    const [isMapReady, setIsMapReady] = useState(false);

    const hasMarkers = useMemo(() => {
        for (const m of manifests) {
            const loc = locationsByManId[m.manId];
            if (loc && typeof loc.lat === "number" && typeof loc.lng === "number") return true;
        }
        return false;
    }, [manifests, locationsByManId]);

    useEffect(() => {
        if (!mapContainer.current || mapRef.current) return;
        const map = new mapboxgl.Map({
            container: mapContainer.current,
            style: "mapbox://styles/mapbox/streets-v11",
            center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM,
        });
        map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
        map.addControl(new mapboxgl.ScaleControl({ maxWidth: 100, unit: "metric" }), "bottom-left");
        mapRef.current = map;
        map.on("load", () => setIsMapReady(true));
        map.on("click", () => setSelectedManId(null));
        return () => {
            try {
                for (const { marker } of markersRef.current.values()) {
                    try { marker.remove(); } catch { }
                }
                markersRef.current.clear();
                map.remove();
            } catch { }
            mapRef.current = null;
            setIsMapReady(false);
            hasAutoFittedRef.current = false;
        };
    }, []);

    const fitAllMarkers = useCallback(({ animate = true } = {}) => {
        const map = mapRef.current;
        if (!map || markersRef.current.size === 0) return;
        const bounds = new mapboxgl.LngLatBounds();
        for (const { lat, lng } of markersRef.current.values()) bounds.extend([lng, lat]);
        if (bounds.isEmpty()) return;
        map.fitBounds(bounds, { padding: 80, maxZoom: 14, duration: animate ? 800 : 0 });
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapReady) return;
        const markers = markersRef.current;
        const present = new Set();

        const personSvg = `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>`;
        const carSvg = `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>`;

        for (const m of manifests) {
            const loc = locationsByManId[m.manId];
            if (!loc || typeof loc.lat !== "number" || typeof loc.lng !== "number") continue;
            present.add(m.manId);

            const speeding = isSpeeding(m);
            const isSelected = m.manId === selectedManId;

            let entry = markers.get(m.manId);
            if (!entry) {
                const wrap = document.createElement("div");
                wrap.className = "gps-marker-wrap";
                const label = document.createElement("div");
                label.className = "gps-marker-label";
                wrap.appendChild(label);
                const dot = document.createElement("div");
                dot.className = "gps-marker-dot";
                wrap.appendChild(dot);

                wrap.addEventListener("click", (ev) => {
                    ev.stopPropagation();
                    setSelectedManId(m.manId);
                });

                const marker = new mapboxgl.Marker({ element: wrap, anchor: "center" })
                    .setLngLat([loc.lng, loc.lat]).addTo(map);

                entry = { marker, wrap, label, dot, lat: loc.lat, lng: loc.lng, snapshot: null };
                markers.set(m.manId, entry);
            }

            if (entry.lat !== loc.lat || entry.lng !== loc.lng) {
                entry.marker.setLngLat([loc.lng, loc.lat]);
                entry.lat = loc.lat; entry.lng = loc.lng;
            }

            const dotClass = `gps-marker-dot ${m.isStale ? "gps-marker-dot--stale"
                : speeding ? "gps-marker-dot--speeding"
                    : "gps-marker-dot--active"}`;
            if (entry.dot.className !== dotClass) entry.dot.className = dotClass;
            entry.wrap.classList.toggle("gps-marker-wrap--selected", isSelected);

            const nombre = m.empNombreCorto || m.empNombre || "Sin conductor";
            const placa = m.vehMatricula || `#${m.manId}`;
            const snap = `${nombre}|${placa}|${speeding}`;
            if (entry.snapshot !== snap) {
                entry.label.innerHTML = `
                  <div class="gps-marker-name">${personSvg}${nombre}</div>
                  <div class="gps-marker-plate ${speeding ? 'gps-marker-plate--speeding' : ''}">${carSvg}${placa}</div>
                `;
                entry.snapshot = snap;
            }
        }

        for (const [manId, entry] of markers) {
            if (!present.has(manId)) {
                try { entry.marker.remove(); } catch { }
                markers.delete(manId);
            }
        }

        if (!hasAutoFittedRef.current && markers.size > 0) {
            fitAllMarkers({ animate: false });
            hasAutoFittedRef.current = true;
        }
    }, [manifests, locationsByManId, isMapReady, selectedManId, fitAllMarkers]);

    useEffect(() => {
        if (!selectedManId) return;
        const map = mapRef.current;
        const entry = markersRef.current.get(selectedManId);
        if (!map || !entry) return;
        map.flyTo({ center: [entry.lng, entry.lat], zoom: Math.max(map.getZoom(), 14), speed: 1.4 });
    }, [selectedManId]);

    const matchedRouteGeojson = useMemo(() => buildMatchedGeojson(selMatched), [selMatched]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapReady) return;
        return whenStyleLoaded(map, () => {
            const sources = {
                "planned-route-src": selPlanned?.geometry ? { type: "Feature", properties: {}, geometry: selPlanned.geometry } : null,
                "live-route-src": selLive?.geometry ? { type: "Feature", properties: {}, geometry: selLive.geometry } : null,
                "track-route-src": matchedRouteGeojson,
            };
            for (const [srcId, data] of Object.entries(sources)) {
                const layerId = srcId.replace("-src", "");
                if (!data) {
                    if (map.getLayer(layerId)) map.setLayoutProperty(layerId, "visibility", "none");
                    continue;
                }
                if (map.getSource(srcId)) map.getSource(srcId).setData(data);
                else {
                    map.addSource(srcId, { type: "geojson", data });
                    if (layerId === "planned-route") {
                        map.addLayer({
                            id: layerId, type: "line", source: srcId,
                            layout: { "line-cap": "round", "line-join": "round" },
                            paint: { "line-width": 5, "line-opacity": 0.75, "line-color": "#1e3a8a" },
                        });
                    } else if (layerId === "live-route") {
                        map.addLayer({
                            id: layerId, type: "line", source: srcId,
                            layout: { "line-cap": "round", "line-join": "round" },
                            paint: { "line-width": 5, "line-opacity": 0.9, "line-color": "#f97316", "line-dasharray": [2, 2] },
                        });
                    } else if (layerId === "track-route") {
                        map.addLayer({
                            id: layerId, type: "line", source: srcId,
                            layout: { "line-cap": "round", "line-join": "round" },
                            paint: {
                                "line-width": 5, "line-opacity": 0.85,
                                "line-color": [
                                    "coalesce", ["get", "color"],
                                    ["case", ["has", "speedKmh"],
                                        ["case",
                                            ["<", ["get", "speedKmh"], 3], "#9CA3AF",
                                            ["<", ["get", "speedKmh"], 30], "#60A5FA",
                                            ["<", ["get", "speedKmh"], 70], "#22C55E",
                                            ["<", ["get", "speedKmh"], 90], "#F59E0B",
                                            ["<", ["get", "speedKmh"], 120], "#EF4444",
                                            "#7F1D1D",
                                        ],
                                        "#0f172a",
                                    ],
                                ],
                            },
                        });
                    }
                }
                map.setLayoutProperty(layerId, "visibility", "visible");
            }
        });
    }, [isMapReady, selectedManId, selPlanned, selLive, matchedRouteGeojson]);

    useEffect(() => {
        if (selectedManId) return;
        const map = mapRef.current;
        if (!map) return;
        for (const layerId of ["planned-route", "live-route", "track-route"]) {
            try { if (map.getLayer(layerId)) map.removeLayer(layerId); } catch { }
        }
        for (const srcId of ["planned-route-src", "live-route-src", "track-route-src"]) {
            try { if (map.getSource(srcId)) map.removeSource(srcId); } catch { }
        }
    }, [selectedManId]);

    const handleSelect = useCallback((manId) => {
        setSelectedManId((prev) => (prev === manId ? null : manId));
    }, []);
    const handleRefresh = useCallback(() => refetch(), [refetch]);
    const handleFilter = useCallback((_, next) => { if (next) setFilterMode(next); }, []);

    const streamConnected = streamStatus === "open";
    const streamErroring = streamStatus === "error";
    const selectedManifest = useMemo(
        () => manifests.find((m) => m.manId === selectedManId) || null,
        [manifests, selectedManId],
    );

    return (
        <>
            <style>{MAP_STYLES}</style>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 440px", gap: 2, height: "calc(100vh - 140px)" }}>
                <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden", boxShadow: 1, bgcolor: "grey.100" }}>
                    <Box ref={mapContainer} sx={{ width: "100%", height: "100%" }} />
                    <Box sx={{ position: "absolute", top: 12, left: 12, display: "flex", flexDirection: "column", gap: 1 }}>
                        <Tooltip title="Ajustar vista" placement="right">
                            <span>
                                <IconButton size="small" onClick={() => fitAllMarkers()} disabled={!hasMarkers}
                                    sx={{ bgcolor: "white", boxShadow: 1 }}>
                                    <CenterFocusStrongIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Refrescar" placement="right">
                            <span>
                                <IconButton size="small" onClick={handleRefresh} disabled={isFetching}
                                    sx={{ bgcolor: "white", boxShadow: 1 }}>
                                    {isFetching ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                    {isMapReady && manifests.length > 0 && !hasMarkers && (
                        <Paper elevation={3} sx={{ position: "absolute", bottom: 16, left: 16, p: 1.5, maxWidth: 320 }}>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <WarningAmberIcon color="warning" fontSize="small" />
                                <Box>
                                    <Typography variant="subtitle2">Sin posiciones para mostrar</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {manifests.length} manifiesto(s) en proceso, ninguno reportó GPS.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    )}
                </Box>

                <Card sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 2, "&:last-child": { pb: 2 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>Sala de Monitoreo</Typography>
                                <Typography variant="caption" color="text.secondary">Manifiestos en ejecución</Typography>
                            </Box>
                            <Tooltip title={streamConnected ? "En vivo" : streamErroring ? (streamError || "Error") : "Conectando…"}>
                                <Chip
                                    size="small"
                                    color={streamConnected ? "success" : streamErroring ? "error" : "default"}
                                    icon={streamConnected ? <SignalCellularAltIcon /> : <SignalCellularOffIcon />}
                                    label={streamConnected ? "En vivo" : streamErroring ? "Sin conexión" : "Conectando"}
                                />
                            </Tooltip>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}>
                            <Badge badgeContent={stats.total} color="primary"><Chip size="small" label="Total" variant="outlined" /></Badge>
                            <Badge badgeContent={stats.active} color="success"><Chip size="small" label="En ruta" variant="outlined" /></Badge>
                            <Badge badgeContent={stats.stale} color="warning"><Chip size="small" label="Sin señal" variant="outlined" /></Badge>
                            <Badge badgeContent={stats.noloc} color="default"><Chip size="small" label="Sin GPS" variant="outlined" /></Badge>
                            {stats.speeding > 0 && (
                                <Badge badgeContent={stats.speeding} color="error">
                                    <Chip size="small" label="Exceso" variant="outlined" icon={<FlashOnIcon />} color="error" />
                                </Badge>
                            )}
                            {stats.withHandover > 0 && (
                                <Badge badgeContent={stats.withHandover} color="info">
                                    <Chip size="small" label="Handover" variant="outlined" icon={<SwapHorizIcon />} />
                                </Badge>
                            )}
                            {stats.totalRemaining > 0 && (
                                <Chip size="small" label={`${stats.totalRemaining} pend.`} variant="outlined" icon={<PlaceIcon />} />
                            )}
                        </Stack>

                        <Divider sx={{ my: 1.5 }} />

                        <TextField
                            size="small" fullWidth
                            placeholder="Matrícula, conductor, origen/destino…"
                            value={query} onChange={(e) => setQuery(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        />

                        <ToggleButtonGroup
                            size="small" exclusive value={filterMode} onChange={handleFilter}
                            sx={{ mt: 1, "& .MuiToggleButton-root": { py: 0.5, px: 1, textTransform: "none", fontSize: 10 } }}
                        >
                            <ToggleButton value="all">Todos</ToggleButton>
                            <ToggleButton value="active">En ruta</ToggleButton>
                            <ToggleButton value="stale">Sin señal</ToggleButton>
                            <ToggleButton value="noloc">Sin GPS</ToggleButton>
                            <ToggleButton value="speeding">Exceso</ToggleButton>
                            <ToggleButton value="handover">Handover</ToggleButton>
                        </ToggleButtonGroup>

                        <Divider sx={{ my: 1.5 }} />

                        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5, mr: -0.5 }}>
                            {error ? <Alert severity="error" sx={{ mb: 1 }}>{error?.message || "Error al cargar."}</Alert> : null}
                            {isLoading && manifests.length === 0 ? (
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, justifyContent: "center" }}>
                                    <CircularProgress size={18} />
                                    <Typography variant="body2" color="text.secondary">Cargando manifiestos…</Typography>
                                </Stack>
                            ) : visibleManifests.length === 0 ? (
                                <Stack spacing={1} alignItems="center" sx={{ mt: 4, opacity: 0.7 }}>
                                    <LocalShippingIcon fontSize="large" color="disabled" />
                                    <Typography variant="body2" color="text.secondary">
                                        {manifests.length === 0 ? "No hay manifiestos en proceso." : "Ningún manifiesto coincide."}
                                    </Typography>
                                </Stack>
                            ) : (
                                <Stack spacing={1}>
                                    {visibleManifests.map((m) => (
                                        <ManifestCard
                                            key={m.manId}
                                            manifest={m}
                                            selected={selectedManId === m.manId}
                                            onSelect={handleSelect}
                                            onOpen={openDetail}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>

                        {selectedManifest && selLive && (
                            <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, bgcolor: alpha('#f97316', 0.06), borderColor: 'warning.light' }}>
                                <Stack spacing={0.5}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                                        Navegación · {selectedManifest.vehMatricula || `#${selectedManifest.manId}`}
                                    </Typography>
                                    <Typography variant="body2">
                                        Próxima: <strong>{selectedManifest.nextStop?.nombre || `#${selLive.nextStopId ?? '—'}`}</strong>
                                    </Typography>
                                    <Typography variant="body2">
                                        Restante: <strong>{((selLive.distance ?? 0) / 1000).toFixed(2)} km</strong> · ETA{" "}
                                        <strong>{((selLive.duration ?? 0) / 60).toFixed(1)} min</strong>
                                    </Typography>
                                </Stack>
                            </Paper>
                        )}

                        <Divider sx={{ mt: 1.5, mb: 1 }} />
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                                {snapshot?.generatedAt ? `Actualizado: ${fmtRelative(snapshot.generatedAt)}` : "—"}
                            </Typography>
                            <Button size="small" startIcon={<RefreshIcon fontSize="small" />} onClick={handleRefresh} disabled={isFetching}>
                                Refrescar
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        </>
    );
};

export default MonitoringDashboard;